import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Cariler.css';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Modal, Form, Row, Col, Button } from 'react-bootstrap';
import IysIzinModal from '../components/IysIzinModal';
import IysKayitListesi, { IysIzinDetay } from '../components/IysKayitListesi';
import IysKilitliAlan from '../components/IysKilitliAlan';
import SmsGonderModal from '../components/SmsGonderModal';
import { useAuth } from '../contexts/AuthContext';

const maskPhone = (phone: string): string => {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 10) {
        return `(0${numbers.slice(0, 3)}) *** ** ${numbers.slice(8)}`;
    }
    return phone.replace(/\d(?=\d{2})/g, '*');
};

const formatPhoneNumber = (phone: string): string => {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 10) {
        return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 8)}-${numbers.slice(8)}`;
    }
    return phone;
};

interface Cari {
    cariID: number;
    takipNo: string;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    aktifMi: boolean;
}

interface CariBakiye {
    cariID: number;
    bakiye: number;
}

interface CariIysIzin {
    iysIzinID: number;
    iletisimTipi: string;
    iletisimVerisi: string;
    izinDurumu: string;
    onayYontemi: string;
    evrakYolu: string | null;
    islemTarihi: string;
    ekleyenAdi: string | null;
}

interface CariForm {
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    aktifMi: boolean;
}

const Cariler: React.FC = () => {
    const { t } = useTranslation();
    const [cariler, setCariler] = useState<Cari[]>([]);
    const { user } = useAuth();
    const [filteredCariler, setFilteredCariler] = useState<Cari[]>([]);
    const [bakiyeler, setBakiyeler] = useState<Record<number, number>>({});
    const [filters, setFilters] = useState({
        cariAdi: '',
        telefon: '',
        durum: 'aktif',
        altKategori: 'all'
    });
    const [altKategoriler, setAltKategoriler] = useState<{cariAltKategoriID: number; altKategoriAdi: string}[]>([]);
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editCari, setEditCari] = useState<Cari | null>(null);
    const [iysIzinler, setIysIzinler] = useState<IysIzinDetay[]>([]);
    const [iysModal, setIysModal] = useState<{ iletisimTipi: 'TELEFON1' | 'TELEFON2' | 'EPOSTA'; iletisimVerisi: string } | null>(null);
    const [iysAktarimAktif, setIysAktarimAktif] = useState(false);
    const [smsCari, setSmsCari] = useState<Cari | null>(null);
    const [formData, setFormData] = useState<CariForm>({
        adiSoyadi: '', firmaAdi: null, telefon1: '', telefon2: null,
        ePosta: null, adres: null, tc: null, cariVD: null, cariVN: null,
        turu: 'Cari', aktifMi: true
    });
    const navigate = useNavigate();

    const handleEditClick = async (e: React.MouseEvent, cariID: number) => {
        e.stopPropagation();
        try {
            const response = await axios.get(`/api/Cariler/${cariID}`);
            const cari = response.data;
            setEditCari(cari);
            setFormData({
                adiSoyadi: cari.adiSoyadi,
                firmaAdi: cari.firmaAdi,
                telefon1: cari.telefon1,
                telefon2: cari.telefon2,
                ePosta: cari.ePosta,
                adres: cari.adres,
                tc: cari.tc,
                cariVD: cari.cariVD,
                cariVN: cari.cariVN,
                turu: cari.turu,
                aktifMi: cari.aktifMi
            });
            setShowEditModal(true);
            try {
                const iysRes = await axios.get(`/api/CariIysIzinleri/cari/${cariID}`);
                setIysIzinler(iysRes.data);
            } catch { setIysIzinler([]); }
        } catch {
            toast.error(t('msg.tedarikciBilgileriYuklenemedi'));
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editCari) return;
        try {
            await axios.put(`/api/Cariler/${editCari.cariID}`, formData);
            toast.success(t('msg.tedarikciBilgileriGuncellendi'));
            setShowEditModal(false);
            fetchCariler();
        } catch (error: any) {
            const msg = error?.response?.data?.message;
            toast.error(msg || t('msg.guncellemeSirasindaHata'));
        }
    };

    const fetchCariler = async () => {
        try {
            const [cariRes, bakiyeRes] = await Promise.all([
                axios.get('/api/Cariler', { params: { tur: 'Cari' } }),
                axios.get('/api/Cariler/bakiyeler', { params: { tur: 'Cari' } }),
            ]);
            setCariler(cariRes.data);
            setFilteredCariler(cariRes.data);
            const map: Record<number, number> = {};
            (bakiyeRes.data as CariBakiye[]).forEach(b => { map[b.cariID] = b.bakiye; });
            setBakiyeler(map);
        } catch (error) {
            toast.error(t('msg.tedarikcilerYuklenirkenHata'));
        }
    };

    useEffect(() => {
        fetchCariler();
        fetchAltKategoriler();
        axios.get('/api/IysAktarimAyarlari/aktif').then(r => setIysAktarimAktif(r.data.aktif)).catch(() => {});
    }, []);

    const fetchAltKategoriler = async () => {
        try {
            const response = await axios.get('/api/CariAltKategorileri/aktif?turu=Cari');
            setAltKategoriler(response.data);
        } catch { /* sessiz */ }
    };

    useEffect(() => {
        let filtered = [...cariler];

        if (filters.cariAdi.trim()) {
            filtered = filtered.filter(cari =>
                cari.adiSoyadi.toLowerCase().includes(filters.cariAdi.toLowerCase().trim()) ||
                (cari.firmaAdi && cari.firmaAdi.toLowerCase().includes(filters.cariAdi.toLowerCase().trim()))
            );
        }

        if (filters.telefon.trim()) {
            const telefonNumbers = filters.telefon.replace(/\D/g, '');
            filtered = filtered.filter(cari =>
                cari.telefon1.replace(/\D/g, '').includes(telefonNumbers)
            );
        }

        if (filters.durum !== 'all') {
            filtered = filtered.filter(cari => {
                if (filters.durum === 'aktif') return cari.aktifMi === true;
                if (filters.durum === 'pasif') return cari.aktifMi === false;
                return true;
            });
        }

        if (filters.altKategori !== 'all') {
            filtered = filtered.filter(cari => cari.turu2 === filters.altKategori);
        }

        setFilteredCariler(filtered);
    }, [filters, cariler]);

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({ cariAdi: '', telefon: '', durum: 'aktif', altKategori: 'all' });
    };

    const handleYeniCari = () => {
        navigate('/cariler/yeni');
    };

    const handleCariClick = (cariID: number) => {
        navigate(`/cariler/${cariID}`);
    };

    const hasActiveFilter =
        filters.cariAdi.trim() !== '' ||
        filters.telefon.trim() !== '' ||
        filters.durum !== 'aktif' ||
        filters.altKategori !== 'all';

    const activeDetailFilterCount = [
        filters.telefon.trim() !== '' ? 1 : 0,
        filters.durum !== 'aktif' ? 1 : 0,
        filters.altKategori !== 'all' ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    return (
        <div className="cariler-page">
            {/* ── Sayfa Başlığı ── */}
            <section className="content-header" style={{ paddingBottom: '0' }}>
                <div className="container-fluid">
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '40px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>{t('cari.baslik')}</h1>
                            <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>{t('cari.altBaslik')}</p>
                        </div>
                        <button
                            onClick={handleYeniCari}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '10px 20px', borderRadius: '8px',
                                background: '#cd6906', color: '#fff',
                                border: 'none', fontSize: '13px', fontWeight: 600,
                                cursor: 'pointer', letterSpacing: '-0.01em',
                                transition: 'background 0.15s, box-shadow 0.15s',
                                boxShadow: '0 4px 12px rgba(205,105,6,0.3)',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = '#b85d05';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.background = '#cd6906';
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            {t('btn.yeniTedarikci')}
                        </button>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid">

                    {/* ── Quick Bar ── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        background: '#fff', border: '1px solid #e9edf2', borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
                        padding: '8px 12px', marginBottom: '8px',
                    }}>
                        {/* Hızlı Ara */}
                        <div style={{ position: 'relative', flex: 1 }}>
                            <i className="fas fa-search" style={{
                                position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                                color: '#94a3b8', fontSize: '11px', pointerEvents: 'none',
                            }}></i>
                            <input
                                type="text"
                                placeholder={t('cari.araPlaceholder')}
                                value={filters.cariAdi}
                                onChange={e => handleFilterChange('cariAdi', e.target.value)}
                                style={{
                                    width: '100%', height: '34px', padding: '0 10px 0 30px',
                                    border: '1px solid #e2e8f0', borderRadius: '8px',
                                    fontSize: '13px', color: '#0f172a', background: '#f8fafc',
                                    boxSizing: 'border-box', outline: 'none',
                                    transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
                                }}
                                onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,.08)'; }}
                                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'none'; }}
                            />
                        </div>
                        {/* Sağ: Kayıt sayısı + Filtreler butonu */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                            <span style={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                {filteredCariler.length} / {cariler.length} {t('tablo.kayit')}
                            </span>
                            <button
                                onClick={() => setFilterPanelOpen(o => !o)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    height: '34px', padding: '0 12px', borderRadius: '8px',
                                    border: `1px solid ${filterPanelOpen ? '#cd6906' : '#e2e8f0'}`,
                                    background: filterPanelOpen ? '#fff8f0' : '#fff',
                                    fontSize: '12.5px', fontWeight: 500,
                                    color: filterPanelOpen ? '#cd6906' : '#4b5563',
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (!filterPanelOpen) { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.background = '#fff8f0'; } }}
                                onMouseLeave={e => { if (!filterPanelOpen) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#4b5563'; e.currentTarget.style.background = '#fff'; } }}
                            >
                                <i className="fas fa-sliders-h" style={{ fontSize: 11 }}></i>
                                {t('btn.filtreler')}
                                {activeDetailFilterCount > 0 && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9999px',
                                        background: '#fff3e0', color: '#cd6906',
                                        fontSize: '10px', fontWeight: 700, lineHeight: 1,
                                    }}>{activeDetailFilterCount}</span>
                                )}
                                <i className={`fas fa-chevron-${filterPanelOpen ? 'up' : 'down'}`} style={{ fontSize: 9, marginLeft: 2 }}></i>
                            </button>
                        </div>
                    </div>

                    {/* ── Accordion Panel ── */}
                    <div style={{
                        maxHeight: filterPanelOpen ? '300px' : '0',
                        overflow: 'hidden',
                        opacity: filterPanelOpen ? 1 : 0,
                        marginBottom: filterPanelOpen ? '12px' : '0',
                        transition: 'max-height 0.28s ease-in-out, opacity 0.2s ease-in-out, margin 0.2s ease-in-out',
                    }}>
                        <div style={{
                            background: '#fafbfc', border: '1px solid #e9edf2', borderRadius: '10px',
                            padding: '14px 14px 10px 14px', marginTop: '4px',
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 12px', alignItems: 'flex-end' }}>
                                {/* Telefon */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#64748b', marginBottom: '2px' }}>{t('label.telefon')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <i className="fas fa-phone" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '10px', pointerEvents: 'none' }}></i>
                                        <input type="text" placeholder="Telefon ara..." value={filters.telefon}
                                            onChange={e => handleFilterChange('telefon', e.target.value)}
                                            style={{ width: '100%', height: '32px', padding: '0 8px 0 26px', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '12px', color: '#0f172a', background: '#fff', boxSizing: 'border-box', outline: 'none' }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                </div>
                                {/* Durum */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#64748b', marginBottom: '2px' }}>{t('label.durum')}</label>
                                    <select value={filters.durum} onChange={e => handleFilterChange('durum', e.target.value)}
                                        style={{ height: '32px', padding: '0 6px', width: '100%', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '12px', color: '#0f172a', background: '#fff', boxSizing: 'border-box', outline: 'none', cursor: 'pointer' }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; }}
                                        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                                    >
                                        <option value="all">{t('label.tumu')}</option>
                                        <option value="aktif">{t('durum.aktif')}</option>
                                        <option value="pasif">{t('durum.pasif')}</option>
                                    </select>
                                </div>
                                {/* Alt Kategori */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: '#64748b', marginBottom: '2px' }}>{t('label.etiketKategori')}</label>
                                    <select value={filters.altKategori} onChange={e => handleFilterChange('altKategori', e.target.value)}
                                        style={{ height: '32px', padding: '0 6px', width: '100%', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '12px', color: '#0f172a', background: '#fff', boxSizing: 'border-box', outline: 'none', cursor: 'pointer' }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; }}
                                        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                                    >
                                        <option value="all">{t('label.tumu')}</option>
                                        {altKategoriler.map(k => (
                                            <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {/* Footer: Temizle */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                <button onClick={clearFilters} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '0 10px', height: '28px', borderRadius: '6px',
                                    border: 'none', background: 'transparent',
                                    fontSize: '12px', color: '#94a3b8', cursor: 'pointer',
                                    transition: 'color 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.color = '#cd6906'}
                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                >
                                    <i className="fas fa-times" style={{ fontSize: 10 }}></i> {t('btn.filtreleriTemizle')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Tablo ── */}
                    <div style={{
                        background: '#fff', borderRadius: '14px',
                        border: '1px solid #f1f5f9',
                        boxShadow: '0 1px 3px rgba(15,23,42,0.07), 0 4px 16px rgba(15,23,42,0.03)',
                        overflow: 'hidden',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none' }}>{t('label.adSoyad')}</th>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none' }}>{t('label.firmaAdi')}</th>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none', width: '160px', whiteSpace: 'nowrap' }}>{t('label.telefon')}</th>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none', width: '90px' }}>{t('label.turu')}</th>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none', textAlign: 'right' }}>{t('label.bakiye')}</th>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none', width: '80px' }}>{t('label.durum')}</th>
                                    <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', color: '#94a3b8', border: 'none', width: '50px', textAlign: 'center' }}>{t('label.islem')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCariler.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '56px 16px', textAlign: 'center', border: 'none' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="#d1d5db" width="64" height="64">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                                                </svg>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#374151', fontSize: '14px', marginBottom: '4px' }}>
                                                        {cariler.length === 0 ? t('msg.henuzTedarikciYok') : t('msg.filtreUygunTedarikciYok')}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                                        {cariler.length === 0 ? t('msg.ilkTedarikciyiEkle') : t('msg.filtreleriDegistirin')}
                                                    </div>
                                                </div>
                                                {cariler.length === 0 && (
                                                    <button
                                                        onClick={handleYeniCari}
                                                        style={{ marginTop: '4px', padding: '8px 18px', borderRadius: '8px', background: '#cd6906', color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                                                    >
                                                        {t('btn.yeniTedarikciEkle')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCariler.map((cari, idx) => {
                                        // Tür badge
                                        const turuBadge = (() => {
                                            const turuStr = (cari.turu || '').toLowerCase();
                                            if (turuStr.includes('müşteri') || turuStr.includes('musteri'))
                                                return { bg: '#fff3e0', color: '#c25e00', label: cari.turu };
                                            if (turuStr.includes('tedarik'))
                                                return { bg: '#f1f5f9', color: '#475569', label: cari.turu };
                                            if (turuStr.includes('arsa') || turuStr.includes('sahip'))
                                                return { bg: '#f0fdf4', color: '#15803d', label: cari.turu };
                                            if (turuStr.includes('taşeron') || turuStr.includes('taseron'))
                                                return { bg: '#faf5ff', color: '#6b21a8', label: cari.turu };
                                            return { bg: '#f1f5f9', color: '#475569', label: cari.turu };
                                        })();

                                        return (
                                        <tr
                                            key={cari.cariID}
                                            onClick={() => handleCariClick(cari.cariID)}
                                            style={{
                                                cursor: 'pointer',
                                                borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9',
                                                transition: 'background 0.1s',
                                            }}
                                            onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = '#fef9f4'}
                                            onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}
                                        >
                                            <td style={{ padding: '15px 16px', border: 'none', fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                                                {cari.adiSoyadi}
                                            </td>
                                            <td style={{ padding: '15px 16px', border: 'none', fontSize: '13px', color: '#64748b' }}>
                                                {cari.firmaAdi || <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </td>
                                            <td style={{ padding: '13px 16px', border: 'none', fontSize: '12.5px', color: '#64748b', fontVariantNumeric: 'tabular-nums', fontFamily: "'Courier New', monospace", letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                                                {maskPhone(cari.telefon1)}
                                            </td>
                                            <td style={{ padding: '13px 16px', border: 'none' }}>
                                                {cari.turu ? (
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center',
                                                        padding: '2px 10px', borderRadius: '9999px',
                                                        fontSize: '11px', fontWeight: 600,
                                                        background: turuBadge.bg, color: turuBadge.color,
                                                        whiteSpace: 'nowrap',
                                                    }}>{turuBadge.label}</span>
                                                ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </td>
                                            <td style={{ padding: '13px 16px', border: 'none', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                {(() => {
                                                    const b = bakiyeler[cari.cariID];
                                                    if (b === undefined) return <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>;
                                                    const sifir = b === 0;
                                                    const pozitif = b > 0;
                                                    // pozitif = tedarikçi alacaklı (biz borçluyuz) → yeşil
                                                    // negatif = tedarikçi borçlu (bize ödemesi var) → kırmızı
                                                    const renk = sifir ? '#94a3b8' : pozitif ? '#16a34a' : '#dc2626';
                                                    const etiket = sifir ? t('label.dengede') : pozitif ? t('label.alacak') : t('label.borc');
                                                    return (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                                            <span style={{ fontWeight: 700, fontSize: 13, color: renk, letterSpacing: '-0.01em' }}>
                                                                {Math.abs(b).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                                                            </span>
                                                            <span style={{ fontSize: 10.5, fontWeight: 600, color: renk, opacity: 0.75 }}>{etiket}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td style={{ padding: '13px 16px', border: 'none' }}>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                    padding: '2px 8px', borderRadius: '100px',
                                                    fontSize: '11px', fontWeight: 600,
                                                    background: cari.aktifMi ? '#f0fdf4' : '#fef2f2',
                                                    color: cari.aktifMi ? '#16a34a' : '#dc2626',
                                                    border: `1px solid ${cari.aktifMi ? '#bbf7d0' : '#fecaca'}`,
                                                }}>
                                                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: cari.aktifMi ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                                                    {cari.aktifMi ? t('durum.aktif') : t('durum.pasif')}
                                                </span>
                                            </td>
                                            <td style={{ padding: '13px 16px', border: 'none', textAlign: 'center' }}>
                                                <button
                                                    onClick={(e) => handleEditClick(e, cari.cariID)}
                                                    style={{
                                                        background: 'transparent', border: 'none',
                                                        padding: '6px 8px', cursor: 'pointer',
                                                        color: '#c4cdd6', fontSize: '13px',
                                                        borderRadius: '6px', transition: 'color 0.15s, background 0.15s',
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.background = '#fff8f0'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.color = '#c4cdd6'; e.currentTarget.style.background = 'transparent'; }}
                                                    title={t('btn.duzenle')}
                                                >
                                                    <i className="fas fa-pen" style={{ fontSize: 11 }}></i>
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSmsCari(cari); }}
                                                    style={{
                                                        background: 'transparent', border: 'none',
                                                        padding: '6px 8px', cursor: 'pointer',
                                                        color: '#c4cdd6', fontSize: '13px',
                                                        borderRadius: '6px', transition: 'color 0.15s, background 0.15s',
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.background = '#eef2ff'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.color = '#c4cdd6'; e.currentTarget.style.background = 'transparent'; }}
                                                    title={t('btn.smsGonder')}
                                                >
                                                    <i className="fas fa-comment-sms" style={{ fontSize: 11 }}></i>
                                                </button>
                                            </td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                </div>
            </section>

            {/* Düzenleme Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('cari.duzenle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.adSoyad')}</Form.Label>
                                    <Form.Control type="text" value={formData.adiSoyadi}
                                        onChange={(e) => setFormData({ ...formData, adiSoyadi: e.target.value })}
                                        required maxLength={100} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.firmaAdi')}</Form.Label>
                                    <Form.Control type="text" value={formData.firmaAdi || ''}
                                        onChange={(e) => setFormData({ ...formData, firmaAdi: e.target.value || null })}
                                        maxLength={100} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-flex align-items-center gap-2">
                                        {t('label.telefon1')}
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                            : formData.telefon1 && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'TELEFON1', iletisimVerisi: formData.telefon1 })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>IYS İzni Al</button>)}
                                    </Form.Label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={editCari?.cariID ?? 0}
                                        takipNo={editCari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel={t('label.telefon1')}
                                        onUnlocked={async () => { try { const r = await axios.get(`/api/CariIysIzinleri/cari/${editCari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <Form.Control type="text" value={formatPhoneNumber(formData.telefon1)}
                                            onChange={(e) => setFormData({ ...formData, telefon1: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                            required maxLength={14} placeholder="(5XX)XXX-XX-XX"
                                            readOnly={readOnly} style={readOnly ? { backgroundColor: '#f9fafb', cursor: 'not-allowed' } : undefined} />
                                    )}</IysKilitliAlan>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-flex align-items-center gap-2">
                                        {t('label.telefon2')}
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                            : formData.telefon2 && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'TELEFON2', iletisimVerisi: formData.telefon2! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>IYS İzni Al</button>)}
                                    </Form.Label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={editCari?.cariID ?? 0}
                                        takipNo={editCari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel={t('label.telefon2')}
                                        onUnlocked={async () => { try { const r = await axios.get(`/api/CariIysIzinleri/cari/${editCari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <Form.Control type="text" value={formData.telefon2 ? formatPhoneNumber(formData.telefon2) : ''}
                                            onChange={(e) => setFormData({ ...formData, telefon2: e.target.value ? e.target.value.replace(/\D/g, '').slice(0, 10) : null })}
                                            maxLength={14} placeholder="(5XX)XXX-XX-XX"
                                            readOnly={readOnly} style={readOnly ? { backgroundColor: '#f9fafb', cursor: 'not-allowed' } : undefined} />
                                    )}</IysKilitliAlan>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-flex align-items-center gap-2">
                                        {t('label.ePosta')}
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                            : formData.ePosta && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'EPOSTA', iletisimVerisi: formData.ePosta! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>IYS İzni Al</button>)}
                                    </Form.Label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={editCari?.cariID ?? 0}
                                        takipNo={editCari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel={t('label.ePosta')}
                                        onUnlocked={async () => { try { const r = await axios.get(`/api/CariIysIzinleri/cari/${editCari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <Form.Control type="email" value={formData.ePosta || ''}
                                            onChange={(e) => setFormData({ ...formData, ePosta: e.target.value || null })}
                                            maxLength={100}
                                            readOnly={readOnly} style={readOnly ? { backgroundColor: '#f9fafb', cursor: 'not-allowed' } : undefined} />
                                    )}</IysKilitliAlan>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.tc')}</Form.Label>
                                    <Form.Control type="text" value={formData.tc || ''}
                                        onChange={(e) => setFormData({ ...formData, tc: e.target.value || null })}
                                        maxLength={11} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vergiDairesi')}</Form.Label>
                                    <Form.Control type="text" value={formData.cariVD || ''}
                                        onChange={(e) => setFormData({ ...formData, cariVD: e.target.value || null })}
                                        maxLength={100} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vergiNo')}</Form.Label>
                                    <Form.Control type="text" value={formData.cariVN || ''}
                                        onChange={(e) => setFormData({ ...formData, cariVN: e.target.value || null })}
                                        maxLength={50} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.tur')}</Form.Label>
                                    <Form.Select value={formData.turu}
                                        onChange={(e) => setFormData({ ...formData, turu: e.target.value })}>
                                        <option value="Cari">Cari</option>
                                        <option value="Tedarikçi">Tedarikçi</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Check type="switch" id="edit-aktif-switch" label="Aktif"
                                        checked={formData.aktifMi}
                                        onChange={(e) => setFormData({ ...formData, aktifMi: e.target.checked })} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.adres')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={formData.adres || ''}
                                        onChange={(e) => setFormData({ ...formData, adres: e.target.value || null })}
                                        maxLength={500} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <IysKayitListesi izinler={iysIzinler} />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>{t('btn.iptal')}</Button>
                        <Button variant="primary" type="submit">{t('btn.kaydet')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {smsCari && (
                <SmsGonderModal
                    onClose={() => setSmsCari(null)}
                    telefonlar={[
                        ...(smsCari.telefon1 ? [{ numara: smsCari.telefon1, etiket: 'Telefon 1' }] : []),
                        ...(smsCari.telefon2 ? [{ numara: smsCari.telefon2, etiket: 'Telefon 2' }] : []),
                    ]}
                    cariID={smsCari.cariID}
                    musteriAdi={smsCari.adiSoyadi}
                />
            )}

            {iysModal && editCari && (
                <IysIzinModal
                    cariID={editCari.cariID}
                    iletisimTipi={iysModal.iletisimTipi}
                    iletisimVerisi={iysModal.iletisimVerisi}
                    onClose={() => setIysModal(null)}
                    onSuccess={async () => {
                        setIysModal(null);
                        try {
                            const r = await axios.get(`/api/CariIysIzinleri/cari/${editCari.cariID}`);
                            setIysIzinler(r.data);
                        } catch {}
                    }}
                />
            )}
        </div>
    );
};

export default Cariler;
